/**
 * Store: Feature Flags
 * ------------------------------------------------------------------------------
 * Global store for Feature Flags.
 *
 * @namespace useFeatureFlagStore
 */
import { defineStore } from 'pinia'

/**
 * Export a Feature Flag global state.
 */
export const useFeatureFlagStore = defineStore({
  id: 'featureFlags',

  state: () => {
    return {
      productCards: {},
    }
  },

  actions: {

    /**
     * Wait for the Swym SDK to be ready.
     * - Fetch the products in the Wishlist.
     */
    setFeatureFlags() {
      const { productCards } = mint.flags

      this.productCards = productCards
    },

    /**
     * Toggle a specific feature flag.
     * @example:
     * mint.EventBus.dispatch('mint:toggle:flag', {
     *  feature: 'productCards.enableQuickAdd',
     *  enabled: true,
     * })
     * @param {Object} payload - Payload.
     * @param {String} payload.feature - String-based dot notation for a feature as
     * defined in `snippets/theme-flags.liquid`
     * @param {Boolean} payload.enabled - Define if the feature is enabled.
     */
    toggleFeatureFlag({ feature, enabled }) {

      /**
       * Convert dot notation to an array (e.g. 'productCards.enableQuickAdd')
       * - Note, only supports for two object levels.
       */
      const objectPath = feature.split('.')

      const category = objectPath[0]
      const name = objectPath[1]

      if (!Object.keys(mint.flags).includes(category)) {
        return
      }

      if (!Object.keys(mint.flags[category]).includes(name)) {
        return
      }

      /**
       * Assign the new value to both the `window.mint` object and
       * the global state, so non-Vue components can also leverage state.
       */
      mint.flags[category][name] = enabled

      this[category] = Object.assign({}, this[category], {
        [name]: enabled,
      })

      /**
       * - Vue components can mapState and subscribe to feature flag changes
       * - Non-Vue components can listen to this event.
       */
      mint.EventBus.dispatch('mint:flag:toggled', {
        category,
        name,
        enabled,
      })
    },
  },
})
